import {Platform} from 'react-native';
import {AppThunk} from '..';
import {GetHolderProfileCabinetResponsePayload, ProvidingListApplication} from '../../../shared/models/api';
import {AuthorizeEsiaResponsePayload} from '../../../shared/models/api/authorizeEsiaResponsePayload';
import {apiUrls} from '../../../shared/constants/urls';
import {httpService, HttpWrapperOptions} from '../../../shared/httpWrapper';
import {getApiRoute} from '../../../shared/pipes';
import {Routes} from '../../../shared/routing/model';
import {storage, StorageKeys} from '../../../shared/storage';
import {clearCardReissueEsiaPending} from '../../../shared/storage/cardReissueEsia';
import {hideGlobalLoader} from '../../../shared/utils/globalLoader';
import {LoginState} from '../../../shared/models/login/loginStep';
import {
  buildCardActivationSnapshot,
  getCardActivationDecision,
  isStoredActivationSnapshotPending,
  mergeCardDeliveryInfo,
} from '../../../screens/login/cardActivation';
import {loginSlice} from '../../reducers/login';
import {profileSlice} from '../../reducers/profile';
import {loginStepSlice} from '../../reducers/runtime/loginStep';

export type EnterCardActivationSessionOptions = {
  /**
   * Already loaded Profile+Applications snapshot.
   * When provided (including null), skips another HolderProfile/Applications round-trip.
   */
  serverRegInfo?: AuthorizeEsiaResponsePayload | null;
};

const mergeActivationRegInfo = (
  storedRegInfo: AuthorizeEsiaResponsePayload,
  serverRegInfo: AuthorizeEsiaResponsePayload | null,
): AuthorizeEsiaResponsePayload => {
  if (!serverRegInfo) {
    return storedRegInfo;
  }

  return {
    ...storedRegInfo,
    cardStatusId: serverRegInfo.cardStatusId ?? storedRegInfo.cardStatusId,
    cardApplicationStatusId: serverRegInfo.cardApplicationStatusId ?? storedRegInfo.cardApplicationStatusId,
    cardDeliveryInfo: mergeCardDeliveryInfo(serverRegInfo.cardDeliveryInfo, storedRegInfo.cardDeliveryInfo),
  };
};

export type LoadCardActivationSnapshotResult = {ok: true; snapshot: AuthorizeEsiaResponsePayload | null} | {ok: false};

const resolveActivationAuthToken = async (authToken?: string | null): Promise<string | null> => {
  if (authToken) {
    return authToken;
  }

  const cabinetToken = await storage.getItem<string>(StorageKeys.token);
  if (cabinetToken) {
    return cabinetToken;
  }

  // Password → activation moves the JWT to activationToken and clears StorageKeys.token.
  return storage.getItem<string>(StorageKeys.activationToken);
};

const loadCardActivationSnapshotResult = async (
  authToken?: string | null,
): Promise<LoadCardActivationSnapshotResult> => {
  try {
    const token = await resolveActivationAuthToken(authToken);
    const authOptions = (wait = true) =>
      new HttpWrapperOptions()
        .Wait(wait)
        .Errors(false)
        .ThrowException(false)
        .Headers(token ? {Authorization: `Bearer ${token}`} : {});
    const holderProfile = await httpService.get<GetHolderProfileCabinetResponsePayload>(
      getApiRoute(apiUrls.getProfile),
      authOptions(),
    );
    const applications = await httpService.get<ProvidingListApplication[]>(
      getApiRoute(apiUrls.getApplications),
      authOptions(),
    );

    if (!holderProfile) {
      return {ok: false};
    }

    const decision = getCardActivationDecision({
      cardStatusId: holderProfile.cardStatusId,
      uscIsBlocked: holderProfile.uscIsBlocked,
      applications,
    });

    return {ok: true, snapshot: buildCardActivationSnapshot(holderProfile, applications, decision)};
  } catch {
    return {ok: false};
  }
};

/** Loads Profile + Applications and applies shared activation decision. Null => enter cabinet. */
export const loadCardActivationSnapshot = async (
  authToken?: string | null,
): Promise<AuthorizeEsiaResponsePayload | null> => {
  const result = await loadCardActivationSnapshotResult(authToken);
  return result.ok ? result.snapshot : null;
};

export const enterCardActivationSession = (options?: EnterCardActivationSessionOptions): AppThunk => {
  return async (dispatch, getState) => {
    hideGlobalLoader({resetBlockingCalls: true});
    await clearCardReissueEsiaPending();

    const cabinetToken = await storage.getItem<string>(StorageKeys.token);
    const serverRegInfo =
      options && 'serverRegInfo' in options
        ? options.serverRegInfo ?? null
        : await loadCardActivationSnapshot(cabinetToken);
    const regInfo = getState().login.esiaRegistrationInfo;
    const nextRegInfo =
      regInfo == null
        ? serverRegInfo
        : {
            ...regInfo,
            cardStatusId: serverRegInfo?.cardStatusId ?? regInfo.cardStatusId,
            cardApplicationStatusId: serverRegInfo?.cardApplicationStatusId ?? regInfo.cardApplicationStatusId,
            cardDeliveryInfo: mergeCardDeliveryInfo(serverRegInfo?.cardDeliveryInfo, regInfo.cardDeliveryInfo),
          };

    if (nextRegInfo) {
      dispatch(loginSlice.actions.setEsiaRegistrationInfo(nextRegInfo));
      await storage.setItem(StorageKeys.esiaRegInfo, nextRegInfo);
    }
    dispatch(loginSlice.actions.setEsiaUserHasNoCards(true));
    dispatch(loginStepSlice.actions.setLoginStep(LoginState.esiaRegCardUnconfirmed));

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const {pathname, origin, hash} = window.location;

      if (pathname === Routes.cardReissue || pathname === `${Routes.cardReissue}/`) {
        window.history.replaceState(window.history.state, '', `${origin}${Routes.login}${hash}`);
      }
    }

    if (cabinetToken) {
      await storage.setItem(StorageKeys.activationToken, cabinetToken);
    }
    await storage.removeItem(StorageKeys.isESIA);
    await storage.removeItem(StorageKeys.token);
    dispatch(profileSlice.actions.reset(null));
    dispatch(profileSlice.actions.setAuthenticationState(false));
    dispatch(profileSlice.actions.setSessionState(false));
    hideGlobalLoader({resetBlockingCalls: true});
  };
};

export const restorePendingCardActivation = (): AppThunk<boolean> => {
  return async (dispatch, getState) => {
    const cabinetToken = await storage.getItem<string>(StorageKeys.token);
    if (!cabinetToken) {
      return false;
    }

    const storedRegInfo = await storage.getItem<AuthorizeEsiaResponsePayload>(StorageKeys.esiaRegInfo);
    const storedIsPending = isStoredActivationSnapshotPending(storedRegInfo);
    const loadResult = await loadCardActivationSnapshotResult();

    // Network/profile unavailable: keep local pending snapshot if any.
    if (!loadResult.ok) {
      if (!storedIsPending || !storedRegInfo) {
        return false;
      }
      dispatch(loginSlice.actions.setEsiaRegistrationInfo(storedRegInfo));
      await dispatch(enterCardActivationSession({serverRegInfo: storedRegInfo}));
      return true;
    }

    const serverRegInfo = loadResult.snapshot;

    // Server is source of truth across devices: activation already done elsewhere → drop stale local snapshot.
    if (!serverRegInfo) {
      const currentRegInfo = getState().login.esiaRegistrationInfo;
      const currentIsPending = isStoredActivationSnapshotPending(currentRegInfo);
      if (currentIsPending) {
        dispatch(loginSlice.actions.setEsiaRegistrationInfo(null));
      }
      if (storedIsPending) {
        const latestStoredRegInfo = await storage.getItem<AuthorizeEsiaResponsePayload>(StorageKeys.esiaRegInfo);
        if (isStoredActivationSnapshotPending(latestStoredRegInfo)) {
          await storage.removeItem(StorageKeys.esiaRegInfo);
        }
      }
      if (!currentRegInfo || currentIsPending) {
        await storage.removeItem(StorageKeys.activationToken);
      }
      return false;
    }

    const latestStoredRegInfo = await storage.getItem<AuthorizeEsiaResponsePayload>(StorageKeys.esiaRegInfo);
    const regInfo = latestStoredRegInfo ? mergeActivationRegInfo(latestStoredRegInfo, serverRegInfo) : serverRegInfo;

    dispatch(loginSlice.actions.setEsiaRegistrationInfo(regInfo));
    await storage.setItem(StorageKeys.esiaRegInfo, regInfo);
    await dispatch(enterCardActivationSession({serverRegInfo}));

    return true;
  };
};

/** Server confirmed a pending issuance/reissue. Leaves the cabinet for the activation screen. */
export const enterServerPendingCardActivation = (): AppThunk<boolean> => {
  return async (dispatch) => {
    const cabinetToken = await storage.getItem<string>(StorageKeys.token);
    if (!cabinetToken) {
      return false;
    }

    const loadResult = await loadCardActivationSnapshotResult();
    if (!loadResult.ok || !loadResult.snapshot) {
      return false;
    }

    const serverRegInfo = loadResult.snapshot;
    dispatch(loginSlice.actions.setEsiaRegistrationInfo(serverRegInfo));
    await storage.setItem(StorageKeys.esiaRegInfo, serverRegInfo);
    await dispatch(enterCardActivationSession({serverRegInfo}));
    return true;
  };
};

export const refreshCardActivationDeliveryInfo = (): AppThunk => {
  return async (dispatch, getState) => {
    const existingRegInfo = getState().login.esiaRegistrationInfo;
    const serverRegInfo = await loadCardActivationSnapshot();
    if (!serverRegInfo?.cardDeliveryInfo?.deliveryTypeId && !serverRegInfo?.cardDeliveryInfo?.trackingNumber) {
      return;
    }

    const nextRegInfo = existingRegInfo ? mergeActivationRegInfo(existingRegInfo, serverRegInfo) : serverRegInfo;

    dispatch(loginSlice.actions.setEsiaRegistrationInfo(nextRegInfo));
    await storage.setItem(StorageKeys.esiaRegInfo, nextRegInfo);
  };
};
